import React, { Component } from 'react';
import { I18n } from 'react-redux-i18n';
import { TreeTable } from 'primereact/treetable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { AutoComplete } from 'primereact/autocomplete';
import { incomingStockService } from '../services/incomingStockService';
import { getQuantityForCount, getMenuItemById } from '../../js/admin';
import moment from 'moment';
import './IncomingStocksTable.css';
import { connect } from 'react-redux';

const flattenMenuItems = (menuCategories) => {
    if (!Array.isArray(menuCategories)) return [];
    let items = [];
    menuCategories.forEach(cat => {
        if (Array.isArray(cat.activeMenuItems)) {
            items = items.concat(cat.activeMenuItems);
        }
        if (Array.isArray(cat.activeMenuCategories)) {
            items = items.concat(flattenMenuItems(cat.activeMenuCategories));
        }
    });
    return items;
};


let lastMenuCategories = null;
let lastFlatProducts = [];
const mapStateToProps = (state) => {
    const menuCategories = state.rootReducer.menuCategories || [];
    if (menuCategories !== lastMenuCategories) {
        lastMenuCategories = menuCategories;
        lastFlatProducts = flattenMenuItems(menuCategories);
    }
    return {
        suppliers: state.rootReducer.suppliers || [],
        products: lastFlatProducts,
        flatProducts: lastFlatProducts,
    };
};

class IncomingStocksTable extends Component {
    state = {
        ...this.state,
    };

        // Status icon cell renderer
            statusBodyTemplate = (rowData) => {
                if (rowData.type === 'entry') return '';
                const entries = rowData.data.entries || [];
                const products = this.props.flatProducts || [];
                let linked = 0, ignored = 0, newCount = 0;
                entries.forEach(entry => {
                    const ignoredEntry = entry.ignored === true;
                    const menuItemId = entry.menuItem && entry.menuItem.id;
                    const hasProduct = products.some(p => p.id === menuItemId);
                    if (hasProduct) {
                        linked++;
                    } else if (ignoredEntry) {
                        ignored++;
                    } else {
                        newCount++;
                    }
                });
                if (rowData.data.draft) {
                    // Draft: Draft, they are not changing the inventory
                    return (
                        <span
                            className="status-icon draft"
                            title={I18n.t('admin_local.status_draft', 'Draft: Draft, they are not changing the inventory')}
                        >📝</span>
                    );
                } else if (newCount > 0) {
                    // New: New products which are not linked yet not ignored, first occurrence
                    return (
                        <span
                            className="status-icon new"
                            title={I18n.t('admin_local.status_new', 'New: New products which are not linked yet not ignored, first occurrence')}
                        >🆕</span>
                    );
                } else if (linked > 0) {
                    // Linked: supplier product code linked with internal product
                    return (
                        <span
                            className="status-icon linked"
                            title={I18n.t('admin_local.status_linked', 'Linked: supplier product code linked with internal product')}
                        >🔗</span>
                    );
                } else if (ignored > 0) {
                    // Ignored: supplier product code was ignored before
                    return (
                        <span
                            className="status-icon ignored"
                            title={I18n.t('admin_local.status_ignored', 'Ignored: supplier product code was ignored before')}
                        >🗑️</span>
                    );
                } else {
                    // No status
                    return (
                        <span
                            className="status-icon"
                            title={I18n.t('admin_local.status_none', 'No status')}
                        >⬜</span>
                    );
                }
            }
    constructor(props) {
        super(props);
        this.state = {
            supplierSuggestions: props.suppliers || [],
            productSuggestions: props.products || [], // products is now always flat menuItems
            productFilterText: '',
            supplierFilterText: '',
            lastFocusedField: null,
            expandedRows: {}
        };
        this.supplierRef = React.createRef();
        this.productRef = React.createRef();
        this.invoiceRef = React.createRef();
        this.deliveryNoteRef = React.createRef();
    }

    componentDidMount() {
        this.initializeFilterText();
    }

    componentDidUpdate(prevProps) {
            if (prevProps.incomingStocks !== this.props.incomingStocks) {
                this.processTableData();
                this.restoreFocus();
            }
            // Update suggestions if redux dropdowns change
            if (prevProps.suppliers !== this.props.suppliers) {
                this.setState({ supplierSuggestions: this.props.suppliers || [] });
            }
            if (prevProps.products !== this.props.products) {
                this.setState({ productSuggestions: this.props.products || [] });
            }

            // Ensure filter text is initialized when filters, suppliers, or products change
            const prevProductsEmpty = !prevProps.products || prevProps.products.length === 0;
            const currProductsPopulated = this.props.products && this.props.products.length > 0;
            if (
                prevProps.filters !== this.props.filters ||
                prevProps.suppliers !== this.props.suppliers ||
                prevProps.products !== this.props.products ||
                (prevProductsEmpty && currProductsPopulated)
            ) {
                this.initializeFilterText();
            }
    }

    // Redux: suppliers and products come from props
    initializeFilterText = () => {
        if (this.props.filters?.supplier && this.props.suppliers) {
            const selectedSupplier = this.props.suppliers.find(s => s.id === this.props.filters.supplier);
            if (selectedSupplier) {
                this.setState({ supplierFilterText: selectedSupplier.name || '' });
            }
        }
        if (this.props.filters?.product && this.props.products) {
            const selectedProduct = this.props.products.find(p => p.id === this.props.filters.product);
            if (selectedProduct) {
                this.setState({ productFilterText: this.formatProductDisplay(selectedProduct) });
            }
        }
    }

    processTableData = () => {
        const { incomingStocks } = this.props;
        
        // Convert incoming stocks to tree table format
        const tableData = incomingStocks.map(stock => {
            const node = {
                key: stock.id,
                data: {
                    id: stock.id,
                    date: stock.date,
                    supplier: stock.supplier,
                    draft: stock.draft,
                    invoice_id: stock.invoice_id,
                    delivery_note_id: stock.delivery_note_id,
                    entry_count: stock.entries ? stock.entries.length : 0,
                    net_price: this.calculateTotalNetPrice(stock.entries || []),
                    entries: stock.entries || [],
                    type: 'stock',
                    expanded: this.state.expandedRows[stock.id] || false
                },
                leaf: stock.entries ? stock.entries.length === 0 : true,
                children: stock.entries ? stock.entries.map(entry => ({
                    key: `entry-${entry.id || Math.random()}`,
                    data: {
                        ...entry,
                        type: 'entry'
                    },
                    leaf: true
                })) : []
            };
            
            return node;
        });
        
        return tableData;
    }

    calculateTotalNetPrice = (entries) => {
        return entries.reduce((total, entry) => total + (entry.net_price || 0), 0);
    }

    onSelectionChange = (e) => {
        const selectedNodeKey = e.value;
        this.setState({ selectedNodeKey });
        
        // Find the selected stock from the tree data
        const { incomingStocks } = this.props;
        const selectedStock = incomingStocks.find(stock => stock.id === selectedNodeKey);
        
        if (selectedStock) {
            this.props.onStockSelect(selectedStock);
        } else {
        }
    }

    findNodeByKey = (key) => {
        const tableData = this.processTableData();
        
        const findNode = (nodes, targetKey) => {
            for (const node of nodes) {
                if (node.key === targetKey) {
                    return node;
                }
                if (node.children) {
                    const found = findNode(node.children, targetKey);
                    if (found) return found;
                }
            }
            return null;
        };
        
        return findNode(tableData, key);
    }

    onRowDoubleClick = (e) => {
        if (this.props.onStockDoubleClick) {
            this.props.onStockDoubleClick();
        }
    }

    // Column renderers
    dateBodyTemplate = (rowData) => {
        if (rowData.type === 'entry') return '';
        return moment(rowData.date).format('DD/MM/YYYY HH:mm');
    }

    supplierBodyTemplate = (rowData) => {
        if (rowData.type === 'entry') return '';
        return rowData.supplier ? rowData.supplier.name : '';
    }

    draftBodyTemplate = (rowData) => {
        if (rowData.type === 'entry') return '';
        const isDraft = rowData.data.draft === true || rowData.data.draft === 'true' || rowData.data.draft === 1;
        return (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <input 
                    type="checkbox" 
                    checked={isDraft}
                    onChange={e => this.handleDraftChange(rowData, e.target.checked)}
                />
            </div>
        );
    }

    handleDraftChange = async (rowData, checked) => {
        // Update draft state in backend
        try {
            const stockKey = rowData.data.stockKey || rowData.data.id;
            await incomingStockService.updateDraftState(stockKey, checked);
            // Locally update the draft value in incomingStocks for immediate UI feedback
            if (Array.isArray(this.props.incomingStocks)) {
                const updatedStocks = this.props.incomingStocks.map(stock =>
                    (stock.id === stockKey ? { ...stock, draft: checked } : stock)
                );
            }
            // Trigger reload of stocks list if parent provides a method
            if (this.props.onReload) {
                this.props.onReload();
            }
        } catch (error) {
            console.error('Failed to update draft state:', error);
        }
    }

    invoiceBodyTemplate = (rowData) => {
        if (rowData.type === 'entry') return '';
        return rowData.invoice_id || '';
    }

    deliveryNoteBodyTemplate = (rowData) => {
        if (rowData.type === 'entry') return '';
        return rowData.delivery_note_id || '';
    }

    entryCountBodyTemplate = (rowData) => {
        if (rowData.type === 'entry') return '';
        const entries = rowData.data.entries || [];
        if (this.props.online_szamla === false) {
            return (
                <div style={{ textAlign: 'right' }}>
                    <span>{entries.length}</span>
                </div>
            );
        }
        const products = this.props.flatProducts || [];
        let linked = 0, ignored = 0, newCount = 0;
        entries.forEach(entry => {
            const ignoredEntry = entry.ignored === true;
            const menuItemId = entry.menuItem && entry.menuItem.id;
            const hasProduct = products.some(p => p.id === menuItemId);
            if (hasProduct) {
                linked++;
            } else if (ignoredEntry) {
                ignored++;
            } else {
                newCount++;
            }
        });
        // Unicode icons: 🔗 linked, 🗑️ ignored, 🆕 new
        const replaceCount = (str, count) => str.replace('{count}', count);
        return (
            <div style={{ textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.5em' }}>
                <span title={replaceCount(I18n.t('admin_local.entries_total'), entries.length)}>{entries.length}</span>
                <span
                    style={{ fontSize: '0.9em', color: '#4caf50' }}
                    title={replaceCount(I18n.t('admin_local.entries_linked'), linked)}
                >🔗 <span className="status-count">{linked}</span></span>
                <span
                    style={{ fontSize: '0.9em', color: '#f44336' }}
                    title={replaceCount(I18n.t('admin_local.entries_ignored'), ignored)}
                >🗑️ <span className="status-count">{ignored}</span></span>
                <span
                    style={{ fontSize: '0.9em', color: '#ff5722', fontWeight: 'bold' }}
                    title={replaceCount(I18n.t('admin_local.entries_new'), newCount)}
                >
                    <span className={newCount > 0 ? "status-icon-new-blink" : undefined}>🆕</span> <span className="status-count">{newCount}</span>
                </span>
            </div>
        );
    }

    netPriceBodyTemplate = (rowData) => {
        if (rowData.type === 'entry') {
            return this.formatNumber(rowData.net_price);
        }
        
        return (
            <input 
                style={{ 
                    textAlign: 'right', 
                    background: 'unset', 
                    border: 'unset', 
                    margin: 'unset' 
                }}
                className="input-numeral"
                type="text"
                value={this.formatNumber(rowData.net_price)}
                disabled
                readOnly
            />
        );
    }

    entriesBodyTemplate = (rowData) => {
        
        if (rowData.type === 'entry') {
            return rowData.data.menuItem ? rowData.data.menuItem.name : '';
        }
        
        // Show entries in the original format using getQuantityForCount
        const entries = rowData.data.entries || [];
        
        if (entries.length === 0) {
            return 'No entries';
        }
        
        // Check if this row is currently selected to show all entries
        const stockKey = rowData.stockKey || rowData._stock?.id;
        const isSelected = this.props.selectedStock && this.props.selectedStock.id === stockKey;
        const displayEntries = isSelected ? entries : entries.slice(0, 2);
        const products = this.props.products || [];
        
        return (
            <div className="entries-container">
                {displayEntries.map((entry, index) => {
                    if (!isSelected && index >= 2) return null; // Safety check
                    
                    // Get menu item ID from entry
                    const menuItemId = entry.menuItem && entry.menuItem.id;
                    if (!menuItemId) {
                        return null;
                    }
                    
                    // Find full menu item from loaded products
                    const fullMenuItem = products.find(product => product.id === menuItemId && product.availableQuantities);
                    if (!fullMenuItem) {
                        return (
                            <div key={index}>Product not found</div>
                        );
                    }
                    
                    try {
                        // Use the same function as the original jQuery implementation
                        const quantityText = getQuantityForCount(fullMenuItem, entry.quantity, entry.count);
                        const menuItemName = fullMenuItem.name || 'Unknown';
                        
                        return (
                            <div key={index}>
                                {quantityText} {menuItemName}
                            </div>
                        );
                    } catch (error) {
                        console.error('Error formatting entry:', error, entry, fullMenuItem);
                        return (
                            <div key={index}>
                                {entry.count} x {entry.quantity} {fullMenuItem.name || 'Unknown'}
                            </div>
                        );
                    }
                })}
                {!isSelected && entries.filter(e => e.menuItem && e.menuItem.id).length > 2 && (
                    <div className="total">&nbsp;...</div>
                )}
            </div>
        );
    }

    otherBodyTemplate = (rowData) => {
        if (rowData.type === 'entry') return '';
        
        const stock = rowData.data._stock;
        if (!stock) return '';
        
        let otherInfo = '';
        
        // Show creation information if available
        if (stock.createdBy) {
            const createdDate = moment(stock.created).format('YYYY-MM-DD HH:mm');
            otherInfo += `<div>${createdDate} - ${stock.createdBy.name} - Created by</div>`;
        }
        
        // Show modification information if available
        if (stock.modifiedBy) {
            const modifiedDate = moment(stock.modified).format('YYYY-MM-DD HH:mm');
            otherInfo += `<div>${modifiedDate} - ${stock.modifiedBy.name} - Modified by</div>`;
        }
        
        return otherInfo ? (
            <div dangerouslySetInnerHTML={{ __html: otherInfo }} />
        ) : '';
    }

    formatNumber = (value) => {
        if (value === null || value === undefined) return '';
        return Number(value).toFixed(2);
    }

    // Filter handlers
    onSupplierFilterChange = (e) => {
        this.props.onFiltersChange({ supplier: e.value });
    }

    onInvoiceFilterChange = (e) => {
        this.props.onFiltersChange({ invoice: e.target.value });
    }

    onDeliveryNoteFilterChange = (e) => {
        this.props.onFiltersChange({ deliveryNote: e.target.value });
    }

    onProductFilterChange = (e) => {
        this.props.onFiltersChange({ product: e.value });
    }

    // Autocomplete search functions
    searchSuppliers = (event) => {
        // Prevent any potential form submission
        if (event && event.originalEvent) {
            event.originalEvent.preventDefault();
        }
        
        const query = event.query;
        const suppliers = this.props.suppliers || [];
        
        if (!query || query.length < 2) {
            // Show all suppliers if query is too short
            this.setState({ supplierSuggestions: suppliers });
        } else {
            // Filter suppliers locally - no API call needed
            const filteredSuppliers = suppliers.filter(supplier => {
                const searchText = query.toLowerCase();
                const supplierName = (supplier.name || '').toLowerCase();
                
                return supplierName.includes(searchText);
            });
            
            this.setState({ supplierSuggestions: filteredSuppliers });
        }
    }

    searchProducts = (event) => {
        // Prevent any potential form submission
        if (event && event.originalEvent) {
            event.originalEvent.preventDefault();
        }
        
        const query = event.query;
        const products = this.props.products || [];
        
        if (!query || query.length < 2) {
            // Show all products if query is too short
            this.setState({ productSuggestions: products });
        } else {
            // Filter products locally - no API call needed
            const filteredProducts = products.filter(product => {
                const searchText = query.toLowerCase();
                const productName = (product.name || '').toLowerCase();
                const manufacturer = (product.manufacturer || '').toLowerCase();
                const label = (product.label || '').toLowerCase();
                
                return productName.includes(searchText) || manufacturer.includes(searchText) || label.includes(searchText);
            });
            
            this.setState({ productSuggestions: filteredProducts });
        }
    }

    // Header templates with filters
    supplierHeaderTemplate = () => {
        const suppliers = this.props.suppliers || [];
        const selectedSupplier = suppliers.find(s => s.id === this.props.filters?.supplier);
        return (
            <div>
                <div>{I18n.t('admin_local.supplier')}</div>
                <div className="hide-print" style={{ position: 'relative' }}>
                    <AutoComplete
                        ref={this.supplierRef}
                        value={this.state.supplierFilterText}
                        suggestions={this.state.supplierSuggestions || []}
                        completeMethod={this.searchSuppliers}
                        field="name"
                        placeholder={I18n.t('admin_local.select_a_supplier')}
                        onChange={this.handleSupplierChange}
                        onSelect={this.handleSupplierSelect}
                        onFocus={() => this.trackFocus('supplier')}
                        style={{ width: '100%' }}
                        forceSelection={false}
                        autoHighlight={true}
                        showClear={true}
                        onClear={this.handleSupplierClear}
                    />
                    {this.state.supplierFilterText && (
                        <Button
                            icon="pi pi-times"
                            className="p-button-text p-button-sm autocomplete-clear-btn"
                            onClick={this.handleSupplierClear}
                            style={{
                                position: 'absolute',
                                right: '8px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: '1rem',
                                height: '1rem',
                                padding: 0,
                                border: 'none',
                                background: 'transparent',
                                color: '#6c757d',
                                fontSize: '12px'
                            }}
                        />
                    )}
                </div>
            </div>
        );
    }

    invoiceHeaderTemplate = () => (
        <div>
            <div>{I18n.t('admin_local.invoice_id')}</div>
            <div className="hide-print">
                <InputText
                    ref={this.invoiceRef}
                    value={this.props.filters?.invoice || ''}
                    onChange={this.onInvoiceFilterChange}
                    onFocus={() => this.trackFocus('invoice')}
                    placeholder={I18n.t('admin_local.invoice_id')}
                    style={{ width: '100%' }}
                />
            </div>
        </div>
    )

    deliveryNoteHeaderTemplate = () => (
        <div>
            <div>{I18n.t('admin_local.delivery_note_id')}</div>
            <div className="hide-print">
                <InputText
                    ref={this.deliveryNoteRef}
                    value={this.props.filters?.deliveryNote || ''}
                    onChange={this.onDeliveryNoteFilterChange}
                    onFocus={() => this.trackFocus('deliveryNote')}
                    placeholder={I18n.t('admin_local.delivery_note_id')}
                    style={{ width: '100%' }}
                />
            </div>
        </div>
    )

    entriesHeaderTemplate = () => {
        const products = this.props.products || [];
        const selectedProduct = products.find(p => p.id === this.props.filters?.product);
        return (
            <div>
                <div>{I18n.t('admin_local.entries')}</div>
                <div className="hide-print" style={{ position: 'relative' }}>
                    <AutoComplete
                        ref={this.productRef}
                        value={this.state.productFilterText}
                        suggestions={this.state.productSuggestions || []}
                        completeMethod={this.searchProducts}
                        field="label"
                        placeholder={I18n.t('admin_local.select_a_product')}
                        onChange={this.handleProductChange}
                        onSelect={this.handleProductSelect}
                        onFocus={() => this.trackFocus('product')}
                        style={{ width: '100%' }}
                        itemTemplate={this.productItemTemplate}
                        forceSelection={false}
                        autoHighlight={true}
                        showClear={true}
                        onClear={this.handleProductClear}
                    />
                    {this.state.productFilterText && (
                        <Button
                            icon="pi pi-times"
                            className="p-button-text p-button-sm autocomplete-clear-btn"
                            onClick={this.handleProductClear}
                            style={{
                                position: 'absolute',
                                right: '8px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                width: '1rem',
                                height: '1rem',
                                padding: 0,
                                border: 'none',
                                background: 'transparent',
                                color: '#6c757d',
                                fontSize: '12px'
                            }}
                        />
                    )}
                </div>
            </div>
        );
    }

    // Custom templates for product AutoComplete
    productItemTemplate = (item) => {
        if (!item) return '';
        
        const manufacturerName = item.manufacturer || '';
        const itemName = item.name || '';
        const categoryName = item.category || '';
        
        return (
            <div>
                <div style={{ fontWeight: 'bold' }}>
                    {manufacturerName} {itemName}
                </div>
                {categoryName && (
                    <div style={{ fontSize: '0.8em', color: '#666' }}>
                        {categoryName}
                    </div>
                )}
            </div>
        );
    }

    productSelectedItemTemplate = (item) => {
        if (!item) return '';
        
        const manufacturerName = item.manufacturer || '';
        const itemName = item.name || '';
        
        return `${manufacturerName} ${itemName}`.trim();
    }

    // Prevent form submission on AutoComplete interactions
    handleProductChange = (e) => {
        // Prevent form submission
        if (e && e.originalEvent) {
            e.originalEvent.preventDefault();
        }
        
        // Update the typed text state to maintain input value
        if (typeof e.value === 'string') {
            // User is typing - update the filter text but don't apply filter
            this.setState({ productFilterText: e.value });
        } else if (e.value && e.value.id) {
            // User selected a valid product - apply filter and update text
            this.setState({ productFilterText: this.formatProductDisplay(e.value) });
            this.props.onFiltersChange({ product: e.value.id });
        }
    }

    formatProductDisplay = (product) => {
        if (!product) return '';
        const manufacturerName = product.manufacturer || '';
        const itemName = product.name || '';
        return `${manufacturerName} ${itemName}`.trim();
    }

    handleProductSelect = (e) => {
        // Prevent form submission
        if (e && e.originalEvent) {
            e.originalEvent.preventDefault();
        }
        
        // Apply filter when user selects a product from dropdown
        if (e.value && e.value.id) {
            this.props.onFiltersChange({ product: e.value.id });
        }
    }

    handleProductClear = (e) => {
        // Prevent form submission
        if (e && e.originalEvent) {
            e.originalEvent.preventDefault();
        }
        this.setState({ productFilterText: '' });
        this.props.onFiltersChange({ product: null });
    }

    handleSupplierChange = (e) => {
        // Prevent form submission
        if (e && e.originalEvent) {
            e.originalEvent.preventDefault();
        }
        
        // Update the typed text state to maintain input value
        if (typeof e.value === 'string') {
            // User is typing - update the filter text but don't apply filter
            this.setState({ supplierFilterText: e.value });
        } else if (e.value && e.value.id) {
            // User selected a valid supplier - apply filter and update text
            this.setState({ supplierFilterText: e.value.name || '' });
            this.props.onFiltersChange({ supplier: e.value.id });
        }
    }

    handleSupplierSelect = (e) => {
        // Prevent form submission
        if (e && e.originalEvent) {
            e.originalEvent.preventDefault();
        }
        
        // Apply filter when user selects a supplier from dropdown
        if (e.value && e.value.id) {
            this.props.onFiltersChange({ supplier: e.value.id });
        }
    }

    handleSupplierClear = (e) => {
        // Prevent form submission
        if (e && e.originalEvent) {
            e.originalEvent.preventDefault();
        }
        this.setState({ supplierFilterText: '' });
        this.props.onFiltersChange({ supplier: null });
    }

    // Handle row expansion toggle for entries column
    onRowToggle = (e) => {
        const { node } = e;
        if (node.data.type === 'stock' || !node.data.type) {
            const expandedRows = { ...this.state.expandedRows };
            expandedRows[node.key] = !expandedRows[node.key];
            
            this.setState({ expandedRows });
        }
    }

    // Focus management methods
    trackFocus = (fieldName) => {
        this.setState({ lastFocusedField: fieldName });
    }

    restoreFocus = () => {
        const { lastFocusedField } = this.state;
        if (lastFocusedField) {
            // Use setTimeout to ensure DOM is ready
            setTimeout(() => {
                try {
                    switch (lastFocusedField) {
                        case 'supplier':
                            if (this.supplierRef.current && this.supplierRef.current.getInput) {
                                this.supplierRef.current.getInput().focus();
                            }
                            break;
                        case 'product':
                            if (this.productRef.current && this.productRef.current.getInput) {
                                this.productRef.current.getInput().focus();
                            }
                            break;
                        case 'invoice':
                            if (this.invoiceRef.current) {
                                this.invoiceRef.current.focus();
                            }
                            break;
                        case 'deliveryNote':
                            if (this.deliveryNoteRef.current) {
                                this.deliveryNoteRef.current.focus();
                            }
                            break;
                    }
                } catch (error) {
                    console.warn('Could not restore focus:', error);
                }
            }, 100);
        }
    }

    formatNumber = (value) => {
        if (value === null || value === undefined) return '';
        return Number(value).toFixed(2);
    }

    renderEntriesPreview = (entries) => {
        if (entries.length === 0) return '';
        
        return (
            <div>
                {entries.slice(0, 3).map((entry, index) => (
                    <div key={index} style={{ fontSize: '0.8em' }}>
                        {entry.menuItem ? entry.menuItem.name : 'Unknown'} ({entry.count})
                    </div>
                ))}
                {entries.length > 3 && (
                    <div style={{ fontSize: '0.8em', fontStyle: 'italic' }}>
                        ...and {entries.length - 3} more
                    </div>
                )}
            </div>
        );
    }

    render() {
        const { incomingStocks, isLoading, selectedStock, onStockSelect, onStockDoubleClick } = this.props;
        
        if (isLoading) {
            return (
                <div className="loading-container">
                    <div className="spinner">Loading...</div>
                </div>
            );
        }

        const products = this.props.products || [];
        const treeData = incomingStocks.map(stock => ({
            key: stock.id,
            data: {
                date: moment(stock.date).format('DD/MM/YYYY HH:mm'),
                supplier: stock.supplier ? stock.supplier.name : '',
                draft: stock.draft,
                invoice: stock.invoice_id || '',
                deliveryNote: stock.delivery_note_id || '',
                entryCount: stock.entries ? stock.entries.length : 0,
                netPrice: this.formatNumber(this.calculateTotalNetPrice(stock.entries || [])),
                entries: stock.entries || [],
                expanded: this.state.expandedRows && this.state.expandedRows[stock.id],
                stockKey: stock.id,
                type: 'stock',
                _stock: stock
            },
            leaf: true
        }));

        
        // For single selection mode, selectionKeys should be the selected key value
        const selectionKeys = selectedStock ? selectedStock.id : null;

        return (
            <div 
                className="incoming-stocks-table" 
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                onSubmit={(e) => {
                    e.preventDefault();
                    return false;
                }}
            >
                <TreeTable 
                    value={treeData}
                    selectionMode="single" 
                    selectionKeys={selectionKeys}
                    onSelectionChange={this.onSelectionChange}
                    onDoubleClick={this.onRowDoubleClick}
                    scrollable
                    scrollHeight="flex"
                    className="incoming-stocks-treetable p-treetable-sm"
                    style={{ flex: 1 }}
                    rowClassName={node => {
                        // ...existing code...
                        const entries = node.data.entries || [];
                        let newCount = 0;
                        const products = this.props.flatProducts || [];
                        entries.forEach(entry => {
                            const ignoredEntry = entry.ignored === true;
                            const menuItemId = entry.menuItem && entry.menuItem.id;
                            const hasProduct = products.some(p => p.id === menuItemId);
                            if (!hasProduct && !ignoredEntry) {
                                newCount++;
                            }
                        });
                        if (newCount > 0) {
                            return 'yellow-row-bg';
                        }
                        return '';
                    }}
                >
                    {this.props.online_szamla !== false && (
                        <Column
                            header={''}
                            body={this.statusBodyTemplate}
                            className="status-icon-cell"
                            style={{ width: '2.5rem', textAlign: 'center' }}
                        />
                    )}
                    <Column 
                        field="date" 
                        header={I18n.t('admin_local.date')} 
                        className="date browser-only"
                        style={{ width: '7.5rem', whiteSpace: 'nowrap' }}
                    />
                    <Column 
                        field="supplier" 
                        header={this.supplierHeaderTemplate} 
                        className="supplier"
                        style={{ minWidth: '12.5rem' }}
                    />
                    <Column 
                        field="draft" 
                        header={I18n.t('admin_local.draft')} 
                        body={this.draftBodyTemplate}
                        style={{ width: '5rem', textAlign: 'center' }}
                    />
                    <Column 
                        field="invoice" 
                        header={this.invoiceHeaderTemplate} 
                        className="invoice browser-only"
                        style={{ width: '9.375rem' }}
                    />
                    <Column 
                        field="deliveryNote" 
                        header={this.deliveryNoteHeaderTemplate} 
                        className="delivery-note browser-only"
                        style={{ width: '9.375rem' }}
                    />
                    <Column 
                        field="entryCount" 
                        header={I18n.t('admin_local.entry_count')} 
                        className="entry-count browser-only"
                        style={{ width: '9rem', textAlign: 'center' }}
                        body={this.entryCountBodyTemplate}
                    />
                    <Column 
                        field="netPrice" 
                        header={I18n.t('admin_local.net_price')} 
                        className="net-price browser-only"
                        style={{ width: '7.5rem', textAlign: 'right' }}
                    />
                    <Column 
                        field="entries" 
                        header={this.entriesHeaderTemplate} 
                        className="entries"
                        style={{ minWidth: '12.5rem' }}
                        body={this.entriesBodyTemplate}
                    />
                    <Column 
                        header={I18n.t('admin_local.other')} 
                        className="other browser-only"
                        style={{ width: '11.25rem' }}
                        body={this.otherBodyTemplate}
                    />
                </TreeTable>
            </div>
        );
    }
}

export default connect(mapStateToProps)(IncomingStocksTable);